import { breakpoints } from '../../../../src/js/breakpoints.ts';
import { countryRegionCodes } from './country-regions.ts';

interface QueryFields {
	keyword: HTMLInputElement | null;
	location: HTMLSelectElement | null;
	travelRequired: HTMLSelectElement | null;
	//department: HTMLSelectElement | null;
}
interface QueryValues {
	keyword: string | undefined;
	location: [string, string] | undefined;
	travelRequired: string | undefined;
	//department: string | undefined;
}
interface Job {
	url: string;
	title: string;
	description: string;
	date: Date;
	country: string;
	region: string;
	locality: string;
	travelRequired: string; // "0" | "1" | ""
	//department: string;
}

class JobListings {
  private isMobile = true;
  private liveJobs: HTMLCollectionOf<Element> | undefined;

  public constructor(elJobListings: HTMLElement) {
    // identify necessary DOM elements
    const elSearch = elJobListings.querySelector('.job-listings__search');
    if (!(elSearch instanceof HTMLElement)) {
      throw new Error('Could not identify job listings search form.');
    }

    const elJobList = elJobListings.querySelector('.job-list');
    if (!(elJobList instanceof HTMLElement)) {
      throw new Error('Could not identify job list to use to render jobs.');
    }
    this.liveJobs = elJobList.getElementsByClassName('job');

    const elTemplate = elJobListings.querySelector('.job-template');
    if (!(elTemplate instanceof HTMLTemplateElement)) {
      throw new Error('Could not identify template to render job listings.');
    }

    this.init({ elSearch, elJobListings, elJobList, elTemplate });
  }

  /**
	 *
	 */
  private init({ elSearch, elJobListings, elJobList, elTemplate }: { elSearch: HTMLElement, elJobListings: HTMLElement, elJobList: HTMLElement, elTemplate: HTMLTemplateElement }): void {
    const filterFields = this.getFilterFields(elSearch);

    // flag that javascript is available/running
    elJobListings.dataset['javascript'] = 'true';

    // identify the source list of jobs originally output to the page
    const sourceJobs = this.getSourceJobs(elJobListings);
    // get list of options for the various <select> filters
    this.populateFilterSelectOptions(filterFields, sourceJobs);

    // bind events to filter field changes
    const filterChange = () => {
      const filters = this.getFilters(filterFields);
      const filteredJobs = this.filterJobs(sourceJobs, filters);
      this.displayJobs(filteredJobs, elJobList, elTemplate);
    };
    filterFields.keyword?.addEventListener('keyup', filterChange);
    filterFields.location?.addEventListener('change', filterChange);
    filterFields.travelRequired?.addEventListener('change', filterChange);
    //filterFields.department?.addEventListener("change", filterChange);

    // bind to media query - if viewport size is >= changeover point, expand all <details> elements
    const mql = matchMedia(`(width >= ${breakpoints.get('md')}rem)`);
    const toggleJobsOpenability = () => {
      this.isMobile = !mql.matches;

      const jobs = [...(this.liveJobs ?? [])];
      this.isMobile
        ? jobs.forEach(job => job.removeAttribute('open'))
        : jobs.forEach(job => job.setAttribute('open', 'open'));
    };

    mql.addEventListener('change', toggleJobsOpenability);
    toggleJobsOpenability();
  }

  /**
	 * Identify base list of jobs (values initially output to the page)
	 */
  private getSourceJobs(elJobListings: HTMLElement): Job[] {
    const elJobs = elJobListings.querySelectorAll('.job');
    return [...elJobs.values()]
      .filter(elJob => elJob instanceof HTMLElement)
      .map(elJob => {
        return {
          url: elJob.querySelector('.job__link')?.getAttribute('href') ?? '',
          title: elJob.querySelector('.job__title')?.textContent ?? '',
          description: elJob.querySelector('.job__description')?.innerHTML ?? '',
          date: new Date(elJob.querySelector('.job__posted-date time')?.getAttribute('datetime') ?? Date.now()),
          country: elJob.querySelector('.job__meta .country')?.textContent ?? '',
          region: elJob.querySelector('.job__meta .region')?.textContent ?? '',
          locality: elJob.querySelector('.job__meta .locality')?.textContent ?? '',
          travelRequired: elJob.querySelector('.job__meta .travel-required')?.textContent ?? '',
          //department: elJob.querySelector(".job__meta .department")?.textContent ?? "",
        };
      });
  }

  /**
	 *
	 */
  private getFilterFields(elSearch: HTMLElement): QueryFields {
    const elKeyword = elSearch.querySelector('input[name=\'keyword\']');
    const elLocation = elSearch.querySelector('select[name=\'location\']');
    const elTravelRequired = elSearch.querySelector('select[name=\'travelRequired\']');
    //const elDepartment = elSearch.querySelector("select[name='department']");
    return {
      keyword: elKeyword instanceof HTMLInputElement ? elKeyword : null,
      location: elLocation instanceof HTMLSelectElement ? elLocation : null,
      travelRequired: elTravelRequired instanceof HTMLSelectElement ? elTravelRequired : null,
      //department: elDepartment instanceof HTMLSelectElement ? elDepartment : null,
    };
  }

  /**
	 * Get the values that will be used to filter the full jobs list
	 */
  private getFilters(queryFields: QueryFields): QueryValues {
    return {
      keyword: queryFields.keyword?.value,
      location: JSON.parse(queryFields.location?.value || '["", ""]'),
      travelRequired: queryFields.travelRequired?.value,
      //department: queryFields.department?.value,
    };
  }

  /**
   * Populate the <select> filters with <option>s based on values used by the full list of jobs
   */
  private populateFilterSelectOptions(filterFields: QueryFields, sourceJobs: Job[]): void {
    // create a list of all the used regions organized by their country
    // create a list of all the used departments
    const usedRegions = new Map<string, Set<string>>();
    //const usedDepartments = new Set<string>();
    for (const sourceJob of sourceJobs) {
      if (!usedRegions.has(sourceJob.country)) {
        usedRegions.set(sourceJob.country, new Set());
      }
      if (sourceJob.region) { // region can possibly be an empty string - don't add empty regions
        usedRegions.get(sourceJob.country)?.add(sourceJob.region);
      }
      //usedDepartments.add(sourceJob.department);
    }

    // create <option> values for the Location filter. Generate a hierarchical list of the used countries -> regions for
    if (filterFields.location) {
      // reset the location select to default options
      filterFields.location.options.length = 0;
      const elDefaultOption = document.createElement('option');
      elDefaultOption.value = '';
      elDefaultOption.textContent = 'All Locations';
      filterFields.location.appendChild(elDefaultOption);

      // alphabetically sort countries
      const sortedCountries = [...usedRegions.entries()].sort((a, b) => {
        const nameA = countryRegionCodes.get(a[0])?.name ?? a[0];
        const nameB = countryRegionCodes.get(b[0])?.name ?? b[0];
        return nameA.localeCompare(nameB);
      });

      // create country-only <option>s
      for (const [countryCode, regionCodes] of sortedCountries) {
        const country = countryRegionCodes.get(countryCode);
        const elCountryOption = document.createElement('option');
        elCountryOption.classList.add('country');
        elCountryOption.value = JSON.stringify([countryCode, '']);
        elCountryOption.textContent = countryRegionCodes.get(countryCode)?.name ?? countryCode;
        filterFields.location.appendChild(elCountryOption);

        // alphabetically sort regions
        const sortedRegions = [...regionCodes].sort((a, b) => {
          const nameA = country?.regions.get(a) ?? a;
          const nameB = country?.regions.get(b) ?? b;
          return nameA.localeCompare(nameB);
        });

        // create country->region <option>s
        for (const regionCode of sortedRegions) {
          const elRegionOption = document.createElement('option');
          elRegionOption.classList.add('region');
          elRegionOption.value = JSON.stringify([countryCode, regionCode]);
          elRegionOption.innerHTML = '&nbsp;&nbsp;&nbsp;&nbsp;' + (country?.regions.get(regionCode) ?? regionCode);
          filterFields.location.appendChild(elRegionOption);
        }
      }
    }

    // create <option> values for the Department filter.
    // if (filterFields.department) {
    // 	// reset the department select to default option
    // 	filterFields.department.options.length = 0;
    // 	const elDefaultOption = document.createElement("option");
    // 	elDefaultOption.value = "";
    // 	elDefaultOption.textContent = "All Departments";
    // 	filterFields.department.appendChild(elDefaultOption);

    // 	for (const usedDepartment of usedDepartments) {
    // 		const elDepartmentOption = document.createElement("option");
    // 		elDepartmentOption.value = usedDepartment;
    // 		elDepartmentOption.textContent = usedDepartment;
    // 		filterFields.department.appendChild(elDepartmentOption);
    // 	}
    // }
  }

  /**
   * Apply the filters to the jobs list to get a subset of all jobs
   */
  private filterJobs(jobs: Job[], filters: QueryValues): Job[] {
    const keyword = (filters.keyword ?? '').trim().toLowerCase();
    const locationCountry = (filters.location?.[0] ?? '').trim().toLowerCase();
    const locationRegion = (filters.location?.[1] ?? '').trim().toLowerCase();
    const travelRequired = filters.travelRequired;
    //const department = (filters.department ?? "")?.trim().toLowerCase();

    // TODO: create match 'score' to order results?
    // - keyword matched in the title                 -> +2 points for each keyword
    // - keywords match in title in consecutive pairs -> +1 point (in addition to regular keyword-title match points)
    // - keyword matched in the body                  -> +1 point for each keyword

    return jobs.filter(job => {
      let isJobIncluded = true;

      if (isJobIncluded && keyword) {
        const jobTitle = job.title.trim().toLowerCase();
        const jobDescription = job.description.trim().toLowerCase();
        isJobIncluded &&= (jobTitle.includes(keyword) || jobDescription.includes(keyword));
      }

      if (isJobIncluded && locationCountry) {
        const jobCountry = job.country.trim().toLowerCase();
        const jobRegion = job.region.trim().toLowerCase();
        isJobIncluded &&= locationRegion
          ? (jobCountry === locationCountry && jobRegion === locationRegion)
          : jobCountry === locationCountry;
      }

      if (isJobIncluded && travelRequired) {
        isJobIncluded &&= job.travelRequired === travelRequired;
      }

      // TODO: ??? department isn't part of the data from Dayforce
      // if (isJobIncluded && department) {
      // 	isJobIncluded &&= job.department.trim().toLowerCase() === department;
      // }

      return isJobIncluded;
    });
  }

  /**
   * Clear any previous jobs on the page and render the provided list of jobs using the provided template
   */
  private displayJobs(jobs: Job[], elJobList: HTMLElement, template: HTMLTemplateElement): void {
    // clear existing jobs list
    elJobList.innerHTML = '';

    if (!jobs.length) {
      const noJobsMessage = document.createElement('p');
      noJobsMessage.classList.add('job-list__msg-none');
      noJobsMessage.innerHTML = 'No jobs match the selected criteria.';
      elJobList.appendChild(noJobsMessage);

      return;
    }

    // sort jobs - newest first
    // - this will override any search/keyword weighted sorting, but that hasn't yet been implemented.
    jobs = jobs.sort((a, b) => {
      if (a.date > b.date) {
        return -1;
      } else if (a.date < b.date) {
        return 1;
      }

      return 0;
    });

    // output jobs list
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    for (const job of jobs) {
      const clone = template.content.cloneNode(true);
      if (!(clone instanceof DocumentFragment)) {
        continue;
      }

      // Replace all instances of 'data-microdata-xxxx' properties with 'xxxx'.
      // - microdata properties are not specified directly in the HTML <template> as they will be picked up by
      //   some microdata-reading services.
      const elsMicrodata = clone.querySelectorAll('[data-microdata-itemscope], [data-microdata-itemtype], [data-microdata-itemprop]');
      for (const el of elsMicrodata) {
        const elAttributes = [...el.attributes]; // shallow-clone the attributes map - it's a live collection and the attributes will be modified inside the loop.
        for (const attribute of elAttributes) {
          if (attribute.name.startsWith('data-microdata-')) {
            const microdataAttribute = attribute.name.replace('data-microdata-', '');
            el.setAttribute(microdataAttribute, attribute.value);
            el.removeAttribute(attribute.name);
          }
        }
      }

      // default items to 'closed' at mobile sizes; open at larger sizes.
      const elJob = clone.querySelector('.job');
      if (elJob) {
        this.isMobile
          ? elJob.removeAttribute('open')
          : elJob.setAttribute('open', 'open');
      }

      const jobChildren = {
        title: clone.querySelector('.job__title'),
        location: clone.querySelector('.job__location'),
        country: clone.querySelector('.job__meta .country'),
        region: clone.querySelector('.job__meta .region'),
        locality: clone.querySelector('.job__meta .locality'),
        postedDate: clone.querySelector('.job__posted-date time'),
        description: clone.querySelector('.job__description'),
        url: clone.querySelector('.job__link'),
      };

      if (jobChildren.title) jobChildren.title.textContent = job.title;
      if (jobChildren.country) jobChildren.country.textContent = job.country;
      if (jobChildren.region) jobChildren.region.textContent = job.region;
      if (jobChildren.locality) jobChildren.locality.textContent = job.locality;
      if (jobChildren.location) jobChildren.location.textContent = [job.locality, job.region, job.country]
        .map(location => location.trim())
        .filter(location => location !== '')
        .join(', ');
      if (jobChildren.postedDate) {
        // calculate all date components as UTC to avoid any timezone shenanigans - only interested in the DATE, no time.
        const year = job.date.getUTCFullYear();
        const month = job.date.getUTCMonth();
        const day = job.date.getUTCDate();

        jobChildren.postedDate.setAttribute('datetime', `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
        jobChildren.postedDate.textContent = `${(monthNames[month] ?? '')} ${day}, ${year}`;
      }
      if (jobChildren.description) jobChildren.description.innerHTML = job.description;
      if (jobChildren.url) jobChildren.url.setAttribute('href', job.url);

      elJobList.appendChild(clone);
    }
  }
}

(() => {
  const onReady = () => {
    const elJobListings = document.querySelector('.job-listings');
    if (elJobListings instanceof HTMLElement) {
      try {
        new JobListings(elJobListings);
      } catch {
        // fail silently
      }
    }
  };

  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', onReady) : onReady();
})();
